import { useCallback, useEffect, useMemo, useState } from "react";
import "./InspectionsTable.css";
import type {
  InspectionState,
  ClosedDataTable,
  ActiveDataTable,
  ClosedInspectionRegister,
  ActiveInspectionRegister,
} from "../InspectionsLists.d";
import TableHeader from "./table-header/TableHeader";
import Pagination from "../Pagination/Pagination";
import ActivedInspections from "./actived-inspections/ActivedInspections";
import ClosedInspections from "./closed-inspections/ClosedInspections";
import { useTranslate } from "@/hooks/use-translate/useTranslate";
import TableSearchers from "./table-searchers/TableSearchers";
import { Icon } from "@/components/icons/Icon";
import SecondaryButton from "@/components/buttons/secondary-button/SecondaryButton";
import { getIsFiltered } from "../utils/inspections.utils";

interface InspectionsTableProps {
  tableType: InspectionState;
  changePage: (
    page: number,
    status: InspectionState | undefined,
    notFromFiltered?: boolean | undefined,
  ) => void;
  tableData: ActiveDataTable | ClosedDataTable;
  searchInspections: (params: string) => void;
  cleanSearch: () => void;
  lastFiltered: string;
  getNoSortedInspections: () => void;
  newPagination?: boolean;
}
const InspectionsTable = ({
  tableType = "active",
  changePage,
  tableData,
  searchInspections,
  cleanSearch,
  getNoSortedInspections,
  newPagination,
  lastFiltered,
}: InspectionsTableProps) => {
  const translate = useTranslate();

  useEffect(() => {
    setRenderedSearchers(false);
  }, [tableType]);

  const [renderedSearchers, setRenderedSearchers] = useState(false);

  const isFiltered = useMemo(() => {
    const hasFilter = getIsFiltered(lastFiltered);
    return hasFilter;
  }, [lastFiltered]);
  const thereIsData = useMemo(() => tableData?.data?.length > 0, [tableData]);

  const restartSearchers = useCallback(() => {
    changePage(0, tableType, true);
    cleanSearch();
  }, [tableType, changePage, cleanSearch]);

  const showSearchers = useCallback(() => {
    if (!thereIsData) return;
    const newState = !renderedSearchers;
    setRenderedSearchers((previousRendered) => !previousRendered);
    if (!newState && isFiltered) restartSearchers();
  }, [isFiltered, renderedSearchers, restartSearchers, thereIsData]);

  return (
    <div className="w-full grid grid-rows-[auto_1fr_auto] min-h-0 px-14 ">
      <div className="shrink-0 w-full h-14 bg-ib-white flex flex-row justify-end items-center">
        {!renderedSearchers ? (
          <button
            type="button"
            className="flex flex-row h-8 px-3 gap-x-2 items-center"
            onClick={showSearchers}
            disabled={!thereIsData}
          >
            <Icon name="searchLens" className="size-4 text-ib-black" />
            <span className="text-ib-main text-ib-gray-darkest">
              {translate("search")}
            </span>
          </button>
        ) : (
          <button
            type="button"
            className="flex flex-row h-8 px-3 gap-x-2 items-center"
            onClick={showSearchers}
          >
            <Icon name="closeEmptyCircle" className="size-4 text-ib-red-main" />
            <span className="text-ib-main text-ib-red-main">
              {translate("search")}
            </span>
          </button>
        )}
      </div>
      <div
        id="scrollable"
        className="h-full min-h-0 overflow-y-auto overflow-x-auto max-w-full"
      >
        <div
          id="scrollable"
          className="border border-l border-ib-gray-lightest rounded-[8px] "
        >
          <table className="w-full">
            <thead>
              <TableHeader
                tableType={tableType}
                canSort={
                  (tableData?.pages && tableData?.pages > 1) ||
                  tableData?.data.length > 1
                    ? true
                    : false
                }
                lastFiltered={lastFiltered}
                searchInspections={searchInspections}
                getNoSortedInspections={getNoSortedInspections}
              />
              <TableSearchers
                tableType={tableType}
                rendered={renderedSearchers}
                closeSearchers={showSearchers}
                searchInspections={searchInspections}
                lastFiltered={lastFiltered}
              />
              {isFiltered && thereIsData && renderedSearchers ? (
                <tr className="bg-ib-white">
                  <th
                    className="p-3 border-b border-b-ib-gray-lightest"
                    colSpan={8}
                  >
                    <div className="w-full flex flex-row justify-center">
                      <SecondaryButton
                        width="content-fit"
                        colour="red"
                        size="medium"
                        icon="closeEmptyCircle"
                        onClick={restartSearchers}
                        label={translate("cleanFilters")}
                      />
                    </div>
                  </th>
                </tr>
              ) : null}
            </thead>
            <tbody>
              {!thereIsData ? (
                <tr
                  data-testid="no-inspection-row"
                  className="table-even-row last-row h-16 border-b border-b-ib-gray-lightest"
                >
                  <td
                    className="px-2 py-8 text-[16px] leading-[18px] text-ib-gray-main text-center"
                    colSpan={8}
                  >
                    {isFiltered
                      ? translate("noSearchResults")
                      : translate(
                          tableType === "active"
                            ? "noOpenInspections"
                            : "noClosedInspections",
                        )}
                    {isFiltered ? (
                      <div className="w-full mt-4 flex flex-row justify-center">
                        <SecondaryButton
                          width="content-fit"
                          colour="red"
                          size="medium"
                          icon="closeEmptyCircle"
                          onClick={restartSearchers}
                          label={translate("cleanFilters")}
                        />
                      </div>
                    ) : null}
                  </td>
                </tr>
              ) : tableType === "active" ? (
                <ActivedInspections
                  tableData={
                    (tableData?.data as ActiveInspectionRegister[]) || []
                  }
                  getNoSortedInspections={getNoSortedInspections}
                />
              ) : (
                <ClosedInspections
                  tableData={
                    (tableData?.data as ClosedInspectionRegister[]) || []
                  }
                />
              )}
            </tbody>
          </table>
        </div>
      </div>
      {tableData.pages && tableData.pages > 1 ? (
        <div className="mt-4 pb-10">
          <Pagination
            lastPage={tableData.pages}
            changePage={changePage}
            newPagination={newPagination}
          />
        </div>
      ) : null}
    </div>
  );
};

export default InspectionsTable;
